import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { styled } from '@mui/material/styles';
import RoomIcon from '@mui/icons-material/Room';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Tooltip from '@mui/material/Tooltip';
import debounce from 'lodash.debounce';
import { errorObject } from './Map';
import { GoogleMap, GoogleMarker, InfoWindow } from './utils';
import {
  Places,
  PlaceResult,
  LatLngLiteral,
  AutocompletePrediction,
  GeocoderAddressComponent,
} from '../../services/places';
import { LocationItem, LocationCallLogMessage } from '../../services/api/types';
import { GOOGLE_MAP_LIST_OF_COUNTRY_CODES } from '../../constants';

interface PrevPlace {
  address: string;
  coordinates: string;
}

export interface CustomAddressControlProps {
  map: GoogleMap;
  marker: GoogleMarker;
  data: LocationItem | null;
  infoWindow: InfoWindow | undefined;
  onPlaceChanged?: (
    place: LocationItem | null,
    logMessage: LocationCallLogMessage,
  ) => void;
  isCountryCodeRestrict: boolean;
}

const Field = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexFlow: 'row',
  backgroundColor: theme.palette.background.default,
  margin: '6px 6px 0px 6px',
  position: 'relative',
}));

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

const Input = styled('input', {
  shouldForwardProp: prop => prop !== 'error',
})<InputProps>(({ theme, error }) => ({
  width: '100%',
  fontSize: theme.typography.body2.fontSize,
  fontFamily: theme.typography.body1.fontFamily,
  border: error ? '2px solid #f21b1bd4' : '2px solid #36bde8',
  outline: 'none',
  padding: '8px 53px 8px 10px',
  borderRadius: '1px',
  color: '#000000',
  '&:focus': {
    border: error ? '2px solid #f21b1bd4' : '1px solid #36bde8',
  },
}));

const RoomIconStyled = styled(RoomIcon)(() => ({
  color: '#23ca40',
  position: 'absolute',
  right: '5px',
  bottom: '7px',
}));

const ErrorIconStyled = styled(ErrorOutlineIcon)(() => ({
  color: '#f21b1bd4',
  position: 'absolute',
  right: '26px',
}));

const TooltipStyled = styled(Tooltip)(() => ({
  '& .MuiTooltip-tooltip': {
    color: '#f21b1bd4',
    background: '#fff',
    border: '1px solid #ddd',
    boxShadow: '-1px 4px 5px 0px #969696',
    padding: '10px',
    fontSize: '0.7rem',
  },
  '& .MuiTooltip-arrow': {
    fontSize: '25px',
    color: '#fff',
  },
}));

const AutocompleteContainer = styled('div')(() => ({
  position: 'relative',
  zIndex: 5,
  width: '100%',
}));

const AutocompleteResults = styled('ul')(() => ({
  margin: '0 auto',
  left: '2px',
  right: '0',
  position: 'absolute',
  backgroundColor: 'white',
  width: 'calc(100% - 12px)',
  padding: '0',
  listStyleType: 'none',
  border: '1px solid #d2d2d2',
  borderTop: '0',
  boxSizing: 'border-box',
  boxShadow: '-1px 4px 5px 0px #969696',
  color: '#000000',
}));

const AutocompleteItem = styled('li')(() => ({
  padding: '9px 5px 9px 35px',
  borderTop: '1px solid #d9d9d9',
  position: 'relative',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  margin: '0',
  fontSize: '12px',
  color: '#000000',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f2f2f2',
    cursor: 'pointer',
    '& .MuiSvgIcon-root': {
      color: '#23ca40',
    },
  },
}));

const AutocompleteIcon = styled(RoomIcon)(() => ({
  display: 'block',
  position: 'absolute',
  top: '6px',
  bottom: '0',
  left: '8px',
  width: '20px',
  height: '20px',
  color: '#666666',
}));

const defaultZoom = 4;
const locZoom = 14;
const defaultCenter = { lat: 39.8097343, lng: -98.5556199 };

export const CustomAddressControl = ({
  map,
  marker,
  infoWindow,
  data,
  onPlaceChanged,
  isCountryCodeRestrict,
}: CustomAddressControlProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const refco = useRef<HTMLInputElement>(null);
  const [width, setWidth] = useState<string>('100px');
  const [center, setCenter] = useState<google.maps.LatLng | undefined>(
    map.getCenter(),
  );
  const [error, setError] = useState<errorObject>({
    error: false,
    showOnlyTooltip: false,
    showLatlngTooltip: false,
    showAddressTooltip: false,
  });

  const formattedCenter = (location: google.maps.LatLng | undefined) => {
    if (!location) {
      return '';
    }
    return `${location.lat()} , ${location.lng()}`;
  };
  const [coordinates, setCoordinates] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [addressSuggestions, setAddressSuggestions] = useState<
    AutocompletePrediction[] | null
  >([]);

  const [prevPlace, setPrevPlace] = useState<PrevPlace>({
    address: '',
    coordinates: '',
  });

  const [addressSelected, setAddressSelected] = useState<string>('');

  marker.addListener('click', () => {
    if (infoWindow) {
      if (infoWindow.get('closed') && address) {
        infoWindow.close();
        infoWindow.setContent(address);
        infoWindow.open(map, marker);
        infoWindow.set('closed', false);
      } else {
        infoWindow.set('closed', true);
      }
    }
  });

  marker.addListener('position_changed', () => {
    const position = marker.getPosition();
    if (position) {
      setCenter(position);
      setCoordinates(formattedCenter(center));
    }
  });

  const getlocationComponents = (result: PlaceResult) => {
    const address_components: GeocoderAddressComponent[] =
      result && result.address_components ? result.address_components : [];
    const street_number = address_components.find(e =>
      e.types.includes('street_number'),
    )?.long_name;
    const street = address_components.find(e =>
      e.types.includes('route'),
    )?.long_name;
    const city = address_components.find(e =>
      e.types.includes('locality'),
    )?.long_name;
    const province = address_components.find(e =>
      e.types.includes('administrative_area_level_2'),
    )?.long_name;
    const state = address_components.find(e =>
      e.types.includes('administrative_area_level_1'),
    )?.short_name;
    const country = address_components.find(e =>
      e.types.includes('country'),
    )?.short_name;
    const postalCode = address_components.find(e =>
      e.types.includes('postal_code'),
    )?.long_name;
    const lat = result?.geometry?.location?.lat();
    const lng = result?.geometry?.location?.lng();
    const location: LocationItem = {
      latitude: lat ? lat : 0,
      longitude: lng ? lng : 0,
      headingDirection: null,
      streetNumber: street_number ? street_number : null,
      street: street ? street : null,
      city: city ? city : null,
      province: province ? province : null,
      state: state ? state : null,
      country: country ? country : null,
      postalCode: postalCode ? postalCode : null,
    };
    return location;
  };

  const formattedAddress = (
    location_components: LocationItem | undefined | null,
  ) => {
    if (!location_components) {
      return '';
    }
    const { streetNumber, street, city, province, state, country, postalCode } =
      location_components;

    return [streetNumber, street, city, province, state, country, postalCode]
      .filter(Boolean)
      .join(', ');
  };

  const geocodeErrorHandler = useCallback(
    async (errorType: any) => {
      if (onPlaceChanged) {
        const logMessage: LocationCallLogMessage = {
          title: 'Location Validation Failed',
          description: 'Reenter the address',
        };
        onPlaceChanged(null, logMessage);
      }
      if (infoWindow) {
        infoWindow.close();
      }
      setCoordinates('');
      setAddress('');
      setError({
        error: true,
        showOnlyTooltip: true,
        showLatlngTooltip: errorType === 'coordinates' ? true : false,
        showAddressTooltip: errorType === 'address' ? true : false,
      });
      marker.setPosition(null);
      map.setCenter(
        new google.maps.LatLng(defaultCenter.lat, defaultCenter.lng),
      );
      map.setZoom(defaultZoom);
    },
    [onPlaceChanged, infoWindow, marker, map],
  );

  const searchByGeocode = useCallback(
    async (request: any, geocodeType: any) => {
      try {
        const mapHelper = new Places();
        const { results, status } = await mapHelper.geocode(request);

        if (
          status === google.maps.GeocoderStatus.OK &&
          results &&
          results.length > 0
        ) {
          const result = results[0];

          if (result?.geometry?.location && result?.address_components) {
            const location: LocationItem = getlocationComponents(result);

            const formatted_address = formattedAddress(location);

            if (formatted_address && formatted_address.length > 3) {
              map.setCenter(result.geometry.location);
              map.setZoom(locZoom);
              marker.setPosition(result.geometry.location);
              setCoordinates(formattedCenter(result.geometry.location));
              setAddress(formatted_address);
              setPrevPlace({
                address: formatted_address,
                coordinates: formattedCenter(result.geometry.location),
              });
              if (infoWindow) {
                infoWindow.setContent(formatted_address);
                infoWindow.open(map, marker);
              }
              if (onPlaceChanged) {
                const logMessage: LocationCallLogMessage = {
                  title: 'Address Successfully Validated',
                  description: '',
                };
                onPlaceChanged(location, logMessage);
              }
            } else {
              geocodeErrorHandler(geocodeType);
            }
          } else {
            geocodeErrorHandler(geocodeType);
          }
        } else {
          geocodeErrorHandler(geocodeType);
        }
      } catch (err) {
        geocodeErrorHandler(geocodeType);
      }
    },
    [map, marker, onPlaceChanged, geocodeErrorHandler, infoWindow],
  );

  const searchAndSetByCoordinates = () => {
    if (
      !refco.current ||
      !refco.current.value ||
      refco.current.value === prevPlace.coordinates
    ) {
      return;
    }
    const latlngValue = refco.current.value.split(',');
    if (latlngValue.length === 2) {
      const lat = Number(latlngValue[0].trim());
      const lng = Number(latlngValue[1].trim());
      if (!lat || !lng) {
        setError({
          error: true,
          showOnlyTooltip: true,
          showLatlngTooltip: true,
          showAddressTooltip: false,
        });
        if (onPlaceChanged) {
          const logMessage: LocationCallLogMessage = {
            title: 'Location Validation Failed',
            description: 'Reenter the address',
          };

          onPlaceChanged(null, logMessage);
        }
        return;
      }
      const latlng: LatLngLiteral = {
        lat: lat,
        lng: lng,
      };
      const requestLatLng = {
        location: latlng,
      };
      searchByGeocode(requestLatLng, 'coordinates');
    } else {
      setError({
        error: true,
        showOnlyTooltip: true,
        showLatlngTooltip: true,
        showAddressTooltip: false,
      });
      if (onPlaceChanged) {
        const logMessage: LocationCallLogMessage = {
          title: 'Location Validation Failed',
          description: 'Reenter the address',
        };
        onPlaceChanged(null, logMessage);
      }
    }
  };

  const searchAndSetByAddress = (currentAddress: string) => {
    if (!currentAddress || currentAddress === prevPlace.address) {
      return;
    }
    const requestAddress = {
      address: currentAddress,
    };
    searchByGeocode(requestAddress, 'address');
  };

  const initAutoComplete = useCallback(async () => {
    if (!ref.current) {
      return;
    }
    const value = ref.current.value;
    const sanitizedValue = value
      .replace(/\\/g, '\\\\') // Escape backslashes first
      .replace(/"/g, '\\"') // Then escape quotes
      .replace(/^\s+|\s+$/g, ''); // Finally trim whitespace

    if (sanitizedValue === '') {
      setCoordinates('');
      marker.setPosition(null);
      setAddressSuggestions([]);
      return;
    }
    if (sanitizedValue.length < 4) {
      setAddressSuggestions([]);
      return;
    }

    // componentRestrictions: is used for allowing and not allowing specific countries.
    // if we want to pass specific country then add  { country: 'us' } or { country: ['us'] }

    const requestAutocomplete = {
      input: sanitizedValue,
      types: ['address'],
      componentRestrictions: {
        country: isCountryCodeRestrict
          ? GOOGLE_MAP_LIST_OF_COUNTRY_CODES
          : null,
      },
      origin: map.getCenter(),
      strictBounds: false,
    };

    try {
      const mapHelper = new Places();
      const { predictions, status } =
        await mapHelper.getPlacePredictions(requestAutocomplete);

      if (status === google.maps.places.PlacesServiceStatus.OK) {
        setAddressSuggestions(predictions);
      } else {
        setAddressSuggestions([]);
      }
    } catch (err) {
      setAddressSuggestions([]);
    }
  }, [isCountryCodeRestrict, map, marker]);

  // we need element to adapt to its container
  const resizeHandler: ResizeObserverCallback = entries => {
    entries.forEach(element => {
      setWidth(`${element.contentRect.width}px`);
    });
  };

  if (window.ResizeObserver) {
    new ResizeObserver(resizeHandler).observe(map.getDiv());
  }

  useEffect(() => {
    const currentAddr = formattedAddress(data);

    if (
      currentAddr !== '' &&
      prevPlace.address !== '' &&
      currentAddr === prevPlace.address
    ) {
      return;
    }
    if (data && data.latitude && data.longitude) {
      const latlng: LatLngLiteral = {
        lat: Number(data.latitude),
        lng: Number(data.longitude),
      };
      const requestLatLng = {
        location: latlng,
      };
      searchByGeocode(requestLatLng, 'coordinates');
    } else {
      // set default center and zoom
      map.setCenter(
        new google.maps.LatLng(defaultCenter.lat, defaultCenter.lng),
      );
      map.setZoom(defaultZoom);
      if (infoWindow) infoWindow.close();
      setAddress('');
      setCoordinates('');
      setPrevPlace({
        address: '',
        coordinates: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchByGeocode, data, map, infoWindow, marker]);

  // search for address/coordinates on enter
  const handleEnter: React.KeyboardEventHandler<HTMLElement> = (el: any) => {
    if (el.key !== 'Enter') {
      return;
    }
    if (el.target.name === 'addressInput' && ref.current && ref.current.value) {
      searchAndSetByAddress(ref.current.value);
      setAddressSuggestions([]);
    }
    if (
      el.target.name === 'coordinatesInput' &&
      refco.current &&
      refco.current.value
    ) {
      searchAndSetByCoordinates();
    }
  };

  const debouncedAddressHandler = useMemo(() => {
    return debounce(initAutoComplete, 300);
  }, [initAutoComplete]);

  useEffect(() => {
    return () => {
      debouncedAddressHandler.cancel();
    };
  }, [debouncedAddressHandler]);

  // Handle input change for address/coordinates field
  const onChangehandler: any = async (e: {
    target: { value: any; name: any };
  }) => {
    if (e.target.name === 'addressInput') {
      debouncedAddressHandler();
      setAddress(e.target.value);
      setCoordinates('');
    }
    if (e.target.name === 'coordinatesInput') {
      setCoordinates(e.target.value);
      setAddress('');
    }
    if (infoWindow) {
      infoWindow.close();
    }
    marker.setPosition(null);
    // set default center
    map.setCenter(new google.maps.LatLng(defaultCenter.lat, defaultCenter.lng));
    map.setZoom(defaultZoom);
    setError({
      error: true,
      showOnlyTooltip: true,
      showLatlngTooltip: false,
      showAddressTooltip: false,
    });
    if (e.target.value === '') {
      setError({
        error: true,
        showOnlyTooltip: false,
        showLatlngTooltip: true,
        showAddressTooltip: true,
      });
      if (onPlaceChanged) {
        const logMessage: LocationCallLogMessage = {
          title: 'Address is Required',
          description: '',
        };
        onPlaceChanged(null, logMessage);
      }
    }
  };

  // Handle address selected from autocomplete list
  const addressSelectedHandler = (currentAddress: AutocompletePrediction) => {
    setAddressSelected(currentAddress.description);
    searchAndSetByAddress(currentAddress.description);
    setAddressSuggestions([]);
  };
  // Handle Blur event for address/coordinates fields
  const debouncedBlurHandler = useMemo(() => {
    return debounce(() => {
      setAddressSuggestions([]);
      if (ref.current && ref.current.value) {
        const value = ref.current.value;
        if (addressSelected === value) {
          setAddressSelected('');
        } else {
          if (value.length > 3) {
            if (value !== prevPlace.address) {
              const requestAddress = {
                address: value,
              };
              searchByGeocode(requestAddress, 'address');
            }
          } else {
            setError({
              error: true,
              showOnlyTooltip: true,
              showLatlngTooltip: false,
              showAddressTooltip: true,
            });
            if (onPlaceChanged) {
              const logMessage: LocationCallLogMessage = {
                title: 'Location Validation Failed',
                description: 'Reenter the address',
              };
              onPlaceChanged(null, logMessage);
            }
          }
        }
      } else {
        setError({
          error: true,
          showOnlyTooltip: false,
          showLatlngTooltip: true,
          showAddressTooltip: true,
        });
      }
    }, 600);
  }, [addressSelected, prevPlace.address, searchByGeocode, onPlaceChanged]);

  useEffect(() => {
    return () => {
      debouncedBlurHandler.cancel();
    };
  }, [debouncedBlurHandler]);

  // Handle Blur event for coordinates fields
  const handleBlur = (e: any) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (
        e.target.name === 'coordinatesInput' &&
        refco.current &&
        refco.current.value
      ) {
        searchAndSetByCoordinates();
      } else {
        setError({
          error: true,
          showOnlyTooltip: false,
          showLatlngTooltip: true,
          showAddressTooltip: true,
        });
      }
    }
  };

  return (
    <div style={{ width }}>
      <AutocompleteContainer>
        <Field>
          <Input
            ref={ref}
            error={(error.error && !error.showOnlyTooltip) || address === ''}
            name="addressInput"
            value={address}
            onKeyUp={handleEnter}
            onChange={onChangehandler}
            onBlur={debouncedBlurHandler}
            tabIndex={0}
            placeholder="Address"
            data-testid="test-addressInput"
          />
          {(error.error && error.showAddressTooltip) ||
            (address === '' && (
              <TooltipStyled
                title={
                  error.showOnlyTooltip
                    ? 'Invalid Address'
                    : 'Address is required'
                }
                arrow
                placement="top"
              >
                <ErrorIconStyled />
              </TooltipStyled>
            ))}
          <RoomIconStyled>
            <RoomIcon />
          </RoomIconStyled>
        </Field>
        {addressSuggestions && addressSuggestions.length > 0 && (
          <AutocompleteResults>
            {addressSuggestions.map((item: AutocompletePrediction) => (
              <AutocompleteItem
                role="presentation"
                key={item.description}
                onClick={() => addressSelectedHandler(item)}
              >
                <AutocompleteIcon />
                <span>{item.description}</span>
              </AutocompleteItem>
            ))}
          </AutocompleteResults>
        )}
      </AutocompleteContainer>

      <Field>
        <Input
          ref={refco}
          error={(error.error && !error.showOnlyTooltip) || coordinates === ''}
          name="coordinatesInput"
          value={coordinates}
          onKeyUp={handleEnter}
          onChange={onChangehandler}
          tabIndex={0}
          onBlur={handleBlur}
          placeholder="Latitude , Longitude"
        />
        {(error.error && error.showLatlngTooltip) ||
          (coordinates === '' && (
            <TooltipStyled
              title={
                error.showOnlyTooltip
                  ? 'Invalid Latitude/Longitude'
                  : 'Latitude/Longitude is required'
              }
              arrow
              placement="top"
            >
              <ErrorIconStyled />
            </TooltipStyled>
          ))}
        <RoomIconStyled>
          <RoomIcon />
        </RoomIconStyled>
      </Field>
    </div>
  );
};
